import { Phone, Video, Info, ThumbsUp, Image, Smile } from 'lucide-react';

interface UtilityTemplateButton {
    type: string;
    text: string;
    url_base?: string;
}

interface PrimaryTemplatePreview {
    title?: string;
    subtitle?: string;
    template_name: string;
    image_url?: string | null;
    thumbnail_url?: string | null;
    buttons?: UtilityTemplateButton[];
}

interface UtilityTemplateMessengerPreviewProps {
    pageName?: string;
    /** Bloco "Foto" — card com imagem/título/subtítulo/botões. Null quando desabilitado. */
    primaryTemplate?: PrimaryTemplatePreview | null;
    /** Bloco "Texto" — mensagem simples complementar ou principal (quando Foto está desabilitada). */
    secondaryText?: string | null;
    /** Usado nos cards da listagem: sem altura fixa, sem footer de input,
     * sem borda/sombra duplicada (o card pai já tem a própria). */
    compact?: boolean;
}

/**
 * Preview de chat Messenger para o Utility Template. Estrutura visual (header/
 * chat/footer) copiada de Broadcast/components/MessengerPreview.tsx, mas com
 * uma API restrita a no máximo 2 "bolhas": 1 card com imagem (bloco Foto) e/ou
 * 1 bolha de texto simples (bloco Texto) — nunca uma lista arbitrária de blocks.
 */
export default function UtilityTemplateMessengerPreview({
    pageName,
    primaryTemplate,
    secondaryText,
    compact = false,
}: UtilityTemplateMessengerPreviewProps) {
    const initials = pageName ? pageName.substring(0, 2).toUpperCase() : 'FB';
    const initial = pageName ? pageName.substring(0, 1).toUpperCase() : 'P';

    const hasPrimary = !!primaryTemplate;
    const hasSecondary = !!secondaryText && secondaryText.trim().length > 0;
    const bubbleCount = (hasPrimary ? 1 : 0) + (hasSecondary ? 1 : 0);

    return (
        <div
            className={`overflow-hidden bg-white flex flex-col ${
                compact ? 'rounded-t-xl' : 'rounded-xl shadow-lg border border-gray-200'
            }`}
            style={compact ? undefined : { height: '520px' }}
        >
            {/* Messenger Header */}
            <div className="bg-white px-3 py-2.5 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                    <div className="relative">
                        <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-xs">{initials}</span>
                        </div>
                        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                    </div>
                    <div>
                        <p className="text-sm font-semibold text-gray-900 leading-tight">{pageName || 'Sua Página'}</p>
                        <p className="text-[10px] text-green-600 font-medium">Ativo agora</p>
                    </div>
                </div>
                {!compact && (
                    <div className="flex items-center gap-1">
                        <button className="p-1.5 rounded-full hover:bg-gray-100 text-blue-600">
                            <Phone className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                        <button className="p-1.5 rounded-full hover:bg-gray-100 text-blue-600">
                            <Video className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                        <button className="p-1.5 rounded-full hover:bg-gray-100 text-blue-600">
                            <Info className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                    </div>
                )}
            </div>

            {/* Chat area */}
            <div className={`bg-white px-3 py-3 space-y-1.5 ${compact ? '' : 'flex-1 overflow-y-auto'}`}>
                {bubbleCount === 0 ? (
                    <div className="flex items-center justify-center h-full">
                        <p className="text-xs text-gray-300">Sem conteúdo</p>
                    </div>
                ) : (
                    <>
                        {/* Date separator */}
                        <div className="flex items-center justify-center mb-2">
                            <span className="text-[10px] text-gray-400 font-medium">
                                {new Date().toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' }).toUpperCase()}
                            </span>
                        </div>

                        {/* Bolha 1 — bloco Foto (card com imagem) */}
                        {hasPrimary && (
                            <div className="flex items-end gap-1.5">
                                {bubbleCount === 1 ? (
                                    <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shrink-0">
                                        <span className="text-white font-bold text-[10px]">{initial}</span>
                                    </div>
                                ) : (
                                    <div className="w-7 shrink-0" />
                                )}
                                <div className="max-w-[220px] space-y-1">
                                    <div
                                        className={`bg-white border border-gray-200 overflow-hidden shadow-sm ${
                                            bubbleCount === 1 ? 'rounded-2xl rounded-bl-md' : 'rounded-2xl rounded-tl-md'
                                        }`}
                                    >
                                        {(primaryTemplate!.thumbnail_url || primaryTemplate!.image_url) && (
                                            <img
                                                src={primaryTemplate!.thumbnail_url || primaryTemplate!.image_url!}
                                                alt="Header"
                                                className="w-full h-28 object-cover"
                                            />
                                        )}
                                        <div className="px-3 py-2">
                                            {primaryTemplate!.title && (
                                                <p className="text-[13px] font-semibold text-gray-900 leading-snug">
                                                    {primaryTemplate!.title}
                                                </p>
                                            )}
                                            {primaryTemplate!.subtitle && (
                                                <p className="text-[12px] text-gray-500 mt-0.5 leading-snug">
                                                    {primaryTemplate!.subtitle}
                                                </p>
                                            )}
                                        </div>
                                        {primaryTemplate!.buttons && primaryTemplate!.buttons.length > 0 && (
                                            <div className="border-t border-gray-100 flex flex-col">
                                                {primaryTemplate!.buttons.map((btn, idx) => (
                                                    <div
                                                        key={idx}
                                                        className={`py-2 px-3 text-center text-[13px] font-medium text-blue-500 hover:bg-gray-50 cursor-pointer ${
                                                            idx !== primaryTemplate!.buttons!.length - 1
                                                                ? 'border-b border-gray-100'
                                                                : ''
                                                        }`}
                                                    >
                                                        {btn.text}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Bolha 2 — bloco Texto (mensagem simples) */}
                        {hasSecondary && (
                            <div className="flex items-end gap-1.5">
                                <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shrink-0">
                                    <span className="text-white font-bold text-[10px]">{initial}</span>
                                </div>
                                <div className="max-w-[220px]">
                                    <div
                                        className={`bg-[#e4e6eb] px-3 py-2 ${
                                            hasPrimary ? 'rounded-2xl rounded-tl-md' : 'rounded-2xl rounded-bl-md'
                                        }`}
                                    >
                                        <p className="text-[13px] text-gray-900 whitespace-pre-wrap break-words leading-snug">
                                            {secondaryText}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Seen indicator */}
                        <div className="flex justify-start ml-9">
                            <span className="text-[10px] text-gray-400">
                                {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                        </div>
                    </>
                )}
            </div>

            {/* Messenger Footer - Input bar */}
            {!compact && (
                <div className="bg-white px-2 py-2 border-t border-gray-100">
                    <div className="flex items-center gap-1">
                        <button className="p-1.5 rounded-full hover:bg-gray-100 text-blue-600 shrink-0">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.5" />
                                <line x1="12" y1="8" x2="12" y2="16" stroke="currentColor" strokeWidth="1.5" />
                                <line x1="8" y1="12" x2="16" y2="12" stroke="currentColor" strokeWidth="1.5" />
                            </svg>
                        </button>
                        <button className="p-1.5 rounded-full hover:bg-gray-100 text-blue-600 shrink-0">
                            <Image className="w-4.5 h-4.5" strokeWidth={1.5} />
                        </button>
                        <button className="p-1.5 rounded-full hover:bg-gray-100 text-blue-600 shrink-0">
                            <Smile className="w-4.5 h-4.5" strokeWidth={1.5} />
                        </button>
                        <div className="flex-1 bg-gray-100 rounded-full px-3 py-1.5 mx-0.5">
                            <p className="text-[13px] text-gray-400">Aa</p>
                        </div>
                        <button className="p-1.5 rounded-full hover:bg-gray-100 text-blue-600 shrink-0">
                            <ThumbsUp className="w-5 h-5" strokeWidth={1.5} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
